import {
  IOrderDetails,
  IFulfillmentGroup,
  IOrderDetailsLineItem,
  IPersonName,
} from '../models/order-details';
import { IActiveDistribution } from '../models/active-orders';
import { DISTRIBUTION_TYPES } from './distribution';

/**
 * Readers for an OMS `orderDetails` response.
 *
 * Nothing here constructs a new object: each function answers one question
 * about the payload it is handed, so the API type flows through the components
 * unchanged. Same contract as `distribution.ts`, against the other query.
 */

/**
 * The card the drawer was opened from, as the group selection reads it.
 * `FulfillmentAddressDetails` has only the card's `distributionType` to hand,
 * so a bare type string is accepted alongside the whole distribution.
 */
export type IOpenedFrom = IActiveDistribution | string;

const asDistribution = (openedFrom?: IOpenedFrom): IActiveDistribution | undefined => {
  return typeof openedFrom === 'string' ? { distributionType: openedFrom } : openedFrom;
};

/**
 * The group holding the line items the card lists.
 *
 * `lineId` is the only per-item identifier both queries carry —
 * `ActiveDistribution.lineItems[].lineId` and
 * `FulfillmentGroup.lineItems[].lineId` are both selected — so a match on it
 * names the group exactly.
 */
const matchGroupByLineId = (
  groups: IFulfillmentGroup[],
  distribution?: IActiveDistribution
): IFulfillmentGroup | undefined => {
  const lineIds = distribution?.lineItems
    ?.map((lineItem) => lineItem?.lineId)
    ?.filter(Boolean) ?? [];

  if (!lineIds.length) return undefined;

  return groups.find(
    (group) => group?.lineItems?.some(
      (lineItem) => lineItem?.lineId && lineIds.includes(lineItem.lineId)
    )
  );
};

/**
 * The coarse fallback for a card whose line ids are missing. A group with a
 * `storePickup` is a pickup whatever its fulfillment code says, which is the
 * same rule `FulfillmentAddressDetails` renders by.
 */
const matchGroupByDistributionType = (
  groups: IFulfillmentGroup[],
  distribution?: IActiveDistribution
): IFulfillmentGroup | undefined => {
  const { distributionType } = distribution ?? {};

  if (distributionType === DISTRIBUTION_TYPES.PICKUP) {
    return groups.find((group) => Boolean(group?.storePickup));
  }

  if (distributionType === DISTRIBUTION_TYPES.DELIVERY) {
    return groups.find((group) => !group?.storePickup);
  }

  return undefined;
};

/**
 * The group the drawer describes.
 *
 * An order can carry several fulfillment groups, and the drawer is opened from
 * one card — one distribution — so the group it describes is that card's, not
 * whichever group happens to have a `storePickup`. On a mixed order (a pickup
 * group and a delivery group) the two disagree about line items, address,
 * stepper, and curbside and locker eligibility, so opening the delivery card
 * used to render the pickup group.
 *
 * Called without a card it keeps the old behaviour — the group with a
 * `storePickup`, else the first — so a caller with no distribution to hand
 * still resolves a group. This is also why the root `isInLocker` is never used:
 * `OrderDetailsAdapterImpl:2425` sets it from `fulfillmentGroups.get(0)` alone,
 * so it is wrong on a mixed order.
 */
export const selectFulfillmentGroup = (
  orderDetails?: IOrderDetails,
  openedFrom?: IOpenedFrom
): IFulfillmentGroup | undefined => {
  const groups = orderDetails?.fulfillmentGroups;

  if (!groups?.length) return undefined;

  const distribution = asDistribution(openedFrom);

  return matchGroupByLineId(groups, distribution)
    ?? matchGroupByDistributionType(groups, distribution)
    ?? groups.find((group) => Boolean(group?.storePickup))
    ?? groups[0];
};

/**
 * Line items always come from the group. Root `lineItems` is nulled by
 * `groupOrderDetails` (`OrderDetailsService:545`) once every group has items.
 */
export const getOrderDetailsLineItems = (
  orderDetails?: IOrderDetails,
  openedFrom?: IOpenedFrom
): IOrderDetailsLineItem[] => {
  return selectFulfillmentGroup(orderDetails, openedFrom)?.lineItems ?? [];
};

/**
 * The status the group's own stepper reads.
 *
 * `FulfillmentGroup` carries no status of its own; its line items do. Items in
 * one group are fulfilled together, so the first item that reports a code
 * stands for the group. The order root is the fallback: it agrees with the
 * group on a single-group order, and on a mixed one it describes whichever
 * group `OrderDetailsAdapterImpl` looked at first.
 */
export const getFulfillmentGroupStatusCode = (
  group?: IFulfillmentGroup,
  orderDetails?: IOrderDetails
): number | undefined => {
  const groupStatusCode = group?.lineItems?.find(
    (lineItem) => lineItem?.statusCode != null
  )?.statusCode;

  return groupStatusCode ?? orderDetails?.statusCode ?? undefined;
};

/**
 * #1897 deletes `setQtyDesc`, which used to fall `itemDescFromCatalog` back to
 * `description` server-side. The fallback lives here now.
 *
 * A blank description counts as absent all the way down the chain: OMS sends
 * `itemDescFromCatalog: ""` rather than omitting it, and `??` would keep it —
 * rendering an empty name where the caller's placeholder belongs.
 */
export const getLineItemName = (lineItem?: IOrderDetailsLineItem) => {
  return [
    lineItem?.itemDescFromCatalog,
    lineItem?.itemDescFromPOS,
    lineItem?.description,
  ].find((name) => Boolean(name?.trim())) ?? undefined;
};

export const getLineItemUnitPrice = (lineItem?: IOrderDetailsLineItem) => {
  return lineItem?.netUnitPrice ?? lineItem?.originalUnitPrice ?? undefined;
};

/**
 * `activeOrders` sends a recipient name as one string; `orderDetails` sends the
 * parts. Both address components render a single string, so join here.
 *
 * Undefined rather than an empty string when there is no name: an empty string
 * still renders a line, and the address blocks omit the line on undefined.
 */
export const getPersonName = (name?: IPersonName) => {
  const joined = [name?.firstName, name?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return joined || undefined;
};

/**
 * The store phone's bare digits, for a `tel:` href. The raw field is not
 * guaranteed to be digits-only (`(770) 555-1234`, `770-555-1234`), so a
 * dialler link is built from this rather than the raw field. Returns undefined
 * for anything that is not a usable ten-digit number.
 */
export const getStorePhoneDigits = (phone?: string | null) => {
  const digits = phone?.replace(/\D/g, '') ?? '';
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  return local.length === 10 ? local : undefined;
};

/**
 * The store phone as the design renders it: `(770)-555-1234`.
 *
 * `formatPhoneNumber` in `general.ts` produces `770-555-1234` and is exported
 * from the package, so this formats separately rather than change what every
 * other consumer already renders. Returns undefined for anything that is not a
 * usable ten-digit number, so the caller can omit the line entirely.
 */
export const formatStorePhone = (phone?: string | null) => {
  const local = getStorePhoneDigits(phone);

  if (!local) return undefined;

  return `(${local.slice(0, 3)})-${local.slice(3, 6)}-${local.slice(6)}`;
};
